import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";

export interface ItemLibraryMetadataEntry {
  userEmail?: string;
  groupName?: string;
  link?: string;
  imageUrl?: string;
  isPublic?: boolean;
  createdBy?: string;
  createdDateTime?: string;
  updatedDateTime?: string;
}

export interface ItemLibraryModel {
  rowKey?: string;
  partitionKey?: string;
  timestamp?: string;
  itemName?: string; // Unique identifier
  metadata?: string; // JSON string containing array of ItemLibraryMetadataEntry
  metadataList?: ItemLibraryMetadataEntry[]; // Deserialized metadata for easier access
  createdDateTime?: string;
  updatedDateTime?: string;
  // For backward compatibility and easier access
  groupName?: string;
  link?: string;
  imageUrl?: string;
  isPublic?: boolean;
  createdBy?: string;
  userEmail?: string; // For private user overrides - identifies which user owns this private item
}

export interface UserItemLibraryOverrideModel {
  rowKey?: string;
  partitionKey?: string;
  timestamp?: string;
  libraryItemRowKey?: string;
  userEmail?: string;
  groupName?: string;
  itemName?: string;
  link?: string;
  imageUrl?: string;
  isPrivate?: boolean;
  createdDateTime?: string;
  updatedDateTime?: string;
}

export interface ItemLibrarySearchModel {
  groupName?: string;
  itemName?: string;
  userEmail?: string;
}

@Injectable({
  providedIn: "root",
})
export class ItemLibraryService {
  private apiUrl = environment.apiBaseUrl + "api/itemlibrary/";

  constructor(private http: HttpClient) {}

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    });
  }

  // Admin only
  createItemLibrary(item: ItemLibraryModel): Observable<ItemLibraryModel> {
    return this.http.post<ItemLibraryModel>(
      this.apiUrl + "create",
      item,
      { headers: this.getHeaders() }
    );
  }

  // Bulk create items
  createBulkItemLibraries(items: ItemLibraryModel[]): Observable<{ Success: boolean; Created: number; Total: number; Items: ItemLibraryModel[] }> {
    return this.http.post<{ Success: boolean; Created: number; Total: number; Items: ItemLibraryModel[] }>(
      this.apiUrl + "createbulk",
      items,
      { headers: this.getHeaders() }
    );
  }

  // Admin only
  updateItemLibrary(item: ItemLibraryModel): Observable<ItemLibraryModel> {
    return this.http.post<ItemLibraryModel>(
      this.apiUrl + "update",
      item,
      { headers: this.getHeaders() }
    );
  }

  // Public
  getItemLibrary(rowKey: string): Observable<ItemLibraryModel> {
    return this.http.get<ItemLibraryModel>(this.apiUrl + "get/" + rowKey);
  }

  // Public
  getAllItemLibraries(): Observable<ItemLibraryModel[]> {
    return this.http.get<ItemLibraryModel[]>(this.apiUrl + "getall");
  }

  // Public - Find exact match by itemName (case-insensitive)
  findByExactItemName(itemName: string): Observable<ItemLibraryModel> {
    return this.http.get<ItemLibraryModel>(this.apiUrl + "findbyitemname/" + encodeURIComponent(itemName));
  }

  // Public
  searchItemLibraries(
    searchModel: ItemLibrarySearchModel
  ): Observable<ItemLibraryModel[]> {
    return this.http.post<ItemLibraryModel[]>(
      this.apiUrl + "search",
      searchModel,
      { headers: this.getHeaders() }
    );
  }

  // Admin only
  deleteItemLibrary(rowKey: string): Observable<any> {
    return this.http.delete<any>(this.apiUrl + "delete/" + rowKey);
  }

  // Authenticated users
  createOrUpdateUserOverride(
    override: UserItemLibraryOverrideModel
  ): Observable<UserItemLibraryOverrideModel> {
    return this.http.post<UserItemLibraryOverrideModel>(
      this.apiUrl + "user-override/create",
      override,
      { headers: this.getHeaders() }
    );
  }

  // Authenticated users
  getUserOverride(
    libraryItemRowKey: string
  ): Observable<UserItemLibraryOverrideModel> {
    return this.http.get<UserItemLibraryOverrideModel>(
      this.apiUrl + "user-override/get/" + libraryItemRowKey
    );
  }

  // Authenticated users
  getUserOverrides(): Observable<UserItemLibraryOverrideModel[]> {
    return this.http.get<UserItemLibraryOverrideModel[]>(
      this.apiUrl + "user-override/getall"
    );
  }

  // Authenticated users
  deleteUserOverride(rowKey: string): Observable<any> {
    return this.http.delete<any>(this.apiUrl + "user-override/delete/" + rowKey);
  }
}

